// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, type MedplumClient, normalizeErrorString } from '@medplum/core';
import type {
  BundleEntry,
  CodeableConcept,
  Condition,
  DocumentReference,
  Encounter,
  MedicationRequest,
  Patient,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconBolt, IconCheck, IconDownload, IconLock } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatDateOnly, parseDateOnly } from '../../utils/dateOnly';
import { fetchPrimaryCoverage, resolvePayorOrganization } from '../../utils/patient-coverage';
import { attachReferralDocumentToTask, createReferralDocument } from '../../utils/referral-pdf';
import { buildOrderSignatureProvenance } from '../../utils/rpm-order-signature';
import { withTenantScope } from '../../utils/tenant';
import { ProviderSelect } from '../tasks/ProviderSelect';
import classes from './ReferralModal.module.css';

const REFERRAL_SYSTEM = 'https://polarissleep.com/fhir/referral-type';
const REFERRAL_TASK_SYSTEM = 'https://polarissleep.com/fhir/task-code';
const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';
const REFERRAL_TYPES = ['Specialist consultation', 'Sleep medicine consultation', 'DME consultation', 'Other'];

export type ReferralDiagnosisGroup = 'This encounter' | 'Existing conditions' | 'Active conditions';

export interface ReferralDiagnosisOption {
  /** Unique select value: `system|code` for coded options, `text|display` for text-only options. */
  value: string;
  display: string;
  group: ReferralDiagnosisGroup;
  /** Original coding with its system preserved. Absent for text-only diagnoses (stored as reasonCode.text). */
  coding?: { system: string; code: string };
}

export interface ReferralNoteDiagnosis {
  code?: string;
  text?: string;
  /** Coding system of `code`. Defaults to ICD-10-CM because encounter note diagnosis inputs are ICD-10 bound. */
  system?: string;
}

type ReferralSection = 'type' | 'reason' | 'signature' | 'date' | 'task';

export interface ReferralModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated?: (order: MedicationRequest) => void;
  patient: Patient;
  /** Encounter context. Omit to open the modal from the patient chart. */
  encounter?: Encounter;
  /** Diagnoses entered on the encounter note form (encounter context only). */
  encounterDiagnoses?: ReferralNoteDiagnosis[];
}

function providerName(practitioner: Practitioner | undefined): string {
  const name = practitioner?.name?.[0];
  return name?.text ?? ([...(name?.given ?? []), name?.family].filter(Boolean).join(' ') || 'Current provider');
}

function patientName(patient: Patient): string {
  const name = patient.name?.[0];
  return name?.text ?? ([...(name?.given ?? []), name?.family].filter(Boolean).join(' ') || 'Patient');
}

export function isActiveCondition(condition: Condition): boolean {
  return condition.clinicalStatus?.coding?.some((coding) => coding.code === 'active') ?? false;
}

function codedOption(
  system: string,
  code: string,
  display: string,
  group: ReferralDiagnosisGroup
): ReferralDiagnosisOption {
  return { value: `${system}|${code}`, display, group, coding: { system, code } };
}

function textOption(display: string, group: ReferralDiagnosisGroup): ReferralDiagnosisOption {
  return { value: `text|${display}`, display, group };
}

function diagnosisFromCondition(
  condition: Condition,
  group: ReferralDiagnosisGroup
): ReferralDiagnosisOption | undefined {
  const codings = condition.code?.coding ?? [];
  const coding =
    codings.find((entry) => entry.system === ICD10_SYSTEM && entry.code) ??
    codings.find((entry) => entry.system && entry.code);
  if (coding?.system && coding.code) {
    return codedOption(coding.system, coding.code, coding.display ?? condition.code?.text ?? coding.code, group);
  }
  // Text-only (or system-less) conditions stay selectable and are stored as reasonCode.text.
  const text =
    condition.code?.text?.trim() ||
    codings.find((entry) => entry.display?.trim())?.display?.trim() ||
    codings.find((entry) => entry.code?.trim())?.code?.trim();
  return text ? textOption(text, group) : undefined;
}

/**
 * Diagnosis choices for a referral. In encounter context the encounter's own diagnoses come first, followed by the
 * patient's active conditions. In chart context (no encounter) only active conditions are offered.
 */
export function buildReferralDiagnosisOptions(
  conditions: Condition[],
  encounter: Encounter | undefined,
  noteDiagnoses: ReferralNoteDiagnosis[] = []
): ReferralDiagnosisOption[] {
  const options: ReferralDiagnosisOption[] = [];
  const seen = new Set<string>();
  const add = (option: ReferralDiagnosisOption | undefined): void => {
    if (option && !seen.has(option.value)) {
      seen.add(option.value);
      options.push(option);
    }
  };
  const activeConditions = conditions.filter(isActiveCondition);

  if (!encounter) {
    for (const condition of activeConditions) {
      add(diagnosisFromCondition(condition, 'Active conditions'));
    }
    return options;
  }

  for (const diagnosis of noteDiagnoses) {
    const code = diagnosis.code?.trim();
    const text = diagnosis.text?.trim();
    if (code) {
      add(codedOption(diagnosis.system ?? ICD10_SYSTEM, code, text || code, 'This encounter'));
    } else if (text) {
      add(textOption(text, 'This encounter'));
    }
  }
  const encounterReference = encounter.id ? `Encounter/${encounter.id}` : undefined;
  const encounterConditionRefs = new Set(
    (encounter.diagnosis ?? []).map((entry) => entry.condition?.reference).filter(Boolean)
  );
  for (const condition of conditions) {
    const linkedToEncounter =
      (condition.id && encounterConditionRefs.has(`Condition/${condition.id}`)) ||
      (encounterReference !== undefined && condition.encounter?.reference === encounterReference);
    if (linkedToEncounter) {
      add(diagnosisFromCondition(condition, 'This encounter'));
    }
  }
  for (const entry of encounter.diagnosis ?? []) {
    const display = entry.condition?.display;
    if (display) {
      add(textOption(display, 'This encounter'));
    }
  }
  for (const condition of activeConditions) {
    add(diagnosisFromCondition(condition, 'Existing conditions'));
  }
  return options;
}

function reasonCodeFor(option: ReferralDiagnosisOption): CodeableConcept {
  return option.coding
    ? { coding: [{ system: option.coding.system, code: option.coding.code, display: option.display }] }
    : { text: option.display };
}

function todayLocalDate(): string {
  return formatDateOnly(new Date()) ?? '';
}

type PdfFailure = 'generate' | 'attach';

interface PdfOutcome {
  failure?: PdfFailure;
  document?: DocumentReference;
  task: Task;
}

function pdfFailureMessage(failure: PdfFailure, error: unknown): string {
  return failure === 'attach'
    ? `Referral order and task were created and the referral PDF was generated, but it could not be attached to the task: ${normalizeErrorString(error)}`
    : `Referral order and task were created, but the referral PDF could not be generated: ${normalizeErrorString(error)}`;
}

const PRIORITIES = [
  { value: 'routine', label: 'Routine', sub: 'within 30 days' },
  { value: 'urgent', label: 'Urgent', sub: 'within 7 days' },
  { value: 'stat', label: 'STAT', sub: 'same day' },
] as const;

type ReferralPriority = (typeof PRIORITIES)[number]['value'];

/** Practice printed in the "Referral From" block; matches the referral PDF. */
const REFERRING_PRACTICE = 'Polaris Sleep';

function practitionerNpi(practitioner: Practitioner | undefined): string | undefined {
  return practitioner?.identifier?.find((item) => item.value && item.system?.toLowerCase().includes('npi'))?.value;
}

function phoneOf(resource: Patient | Practitioner | undefined): string | undefined {
  return resource?.telecom?.find((item) => item.system === 'phone' && item.value)?.value;
}

function patientMrn(patient: Patient): string | undefined {
  return patient.identifier?.find((item) => item.type?.coding?.some((coding) => coding.code === 'MR'))?.value;
}

function formatDisplayDate(value: string | undefined): string | undefined {
  const parsed = value ? dayjs(value) : undefined;
  return parsed?.isValid() ? parsed.format('MMM D, YYYY') : undefined;
}

function encounterTitle(encounter: Encounter): string {
  return encounter.type?.[0]?.text ?? encounter.type?.[0]?.coding?.[0]?.display ?? 'Encounter';
}

async function loadInsuranceSummary(medplum: MedplumClient, patientId: string): Promise<string | undefined> {
  const coverage = await fetchPrimaryCoverage(medplum, patientId);
  if (!coverage) {
    return undefined;
  }
  const payor = await resolvePayorOrganization(medplum, coverage);
  return [payor?.name, coverage.subscriberId].filter(Boolean).join(' · ') || undefined;
}

type FieldVariant = 'readonly' | 'mono';

/** Mantine input class names for the design's `.fld` / `.finput` field vocabulary. */
function fieldClassNames(
  options: { invalid?: boolean; variants?: FieldVariant[]; span2?: boolean; textarea?: boolean } = {}
): {
  root: string;
  label: string;
  input: string;
  description: string;
  required: string;
} {
  return {
    root: [
      classes.field,
      options.invalid ? classes.invalid : undefined,
      options.span2 ? classes.span2 : undefined,
      ...(options.variants ?? []).map((variant) => classes[variant]),
    ]
      .filter(Boolean)
      .join(' '),
    label: classes.label,
    input: options.textarea ? `${classes.input} ${classes.textarea}` : classes.input,
    description: classes.hint,
    required: classes.required,
  };
}

/** The design puts hints under the control. */
const FIELD_ORDER: ('label' | 'input' | 'description' | 'error')[] = ['label', 'input', 'description', 'error'];

function referralTaskTitle(referralType: string): string {
  return `Process ${referralType} referral`;
}

export function ReferralModal({
  opened,
  onClose,
  onCreated,
  patient,
  encounter,
  encounterDiagnoses,
}: ReferralModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  // The signed-in provider is always the requester and signer, on every surface (e.g. a covering NP in a session).
  const currentPractitioner = profile?.resourceType === 'Practitioner' ? profile : undefined;
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [insurance, setInsurance] = useState<string | undefined>();
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [referralType, setReferralType] = useState<string | null>(null);
  const [referralTarget, setReferralTarget] = useState('');
  const [priority, setPriority] = useState<ReferralPriority>('routine');
  const [reason, setReason] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [recipient, setRecipient] = useState<Reference<Practitioner> | undefined>();
  /** Time the provider clicked the signature pad; undefined while unsigned. */
  const [signedAt, setSignedAt] = useState<string | undefined>();
  const [orderDate, setOrderDate] = useState(todayLocalDate);
  const [invalidSection, setInvalidSection] = useState<ReferralSection | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<MedicationRequest | undefined>();
  const [pdf, setPdf] = useState<PdfOutcome | undefined>();
  const [retryingPdf, setRetryingPdf] = useState(false);

  const diagnoses = useMemo(
    () => buildReferralDiagnosisOptions(conditions, encounter, encounterDiagnoses),
    [conditions, encounter, encounterDiagnoses]
  );

  useEffect(() => {
    if (!opened) {
      return;
    }
    setCreatedOrder(undefined);
    setPdf(undefined);
    setInvalidSection(undefined);
    setDiagnosis(null);
    setReason('');
    setClinicalNotes('');
    setTaskNotes('');
    setReferralType(null);
    setReferralTarget('');
    setPriority('routine');
    setRecipient(undefined);
    setSignedAt(undefined);
    setOrderDate(todayLocalDate());
    setInsurance(undefined);
    if (!patient.id) {
      return;
    }
    medplum
      .searchResources('Condition', { patient: `Patient/${patient.id}`, _count: '100' })
      .then((loaded) => setConditions(loaded))
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setConditions([]);
        showNotification({ color: 'red', message: normalizeErrorString(error) });
      });
    // Insurance is display-only context; a lookup failure leaves the field empty.
    loadInsuranceSummary(medplum, patient.id)
      .then(setInsurance)
      .catch((error: unknown) => Sentry.captureException(error));
  }, [medplum, opened, patient.id]);

  // Encounter context preselects the first encounter diagnosis; chart context leaves the optional diagnosis empty.
  const defaultDiagnosis = encounter ? diagnoses.find((entry) => entry.group === 'This encounter')?.value : undefined;
  useEffect(() => {
    if (opened && defaultDiagnosis) {
      setDiagnosis((current) => current ?? defaultDiagnosis);
    }
  }, [defaultDiagnosis, opened]);

  const diagnosisOptions = useMemo(() => {
    const toItem = (entry: ReferralDiagnosisOption): { value: string; label: string } => ({
      value: entry.value,
      label: entry.coding ? `${entry.display} (${entry.coding.code})` : entry.display,
    });
    if (!encounter) {
      return diagnoses.map(toItem);
    }
    return (['This encounter', 'Existing conditions'] as const)
      .map((group) => ({ group, items: diagnoses.filter((entry) => entry.group === group).map(toItem) }))
      .filter((entry) => entry.items.length > 0);
  }, [diagnoses, encounter]);

  const selectedDiagnosis = diagnoses.find((entry) => entry.value === diagnosis);
  const signerName = providerName(currentPractitioner);
  const mrn = patientMrn(patient);

  function fail(section: ReferralSection, message: string): void {
    setInvalidSection(section);
    window.setTimeout(() => setInvalidSection((current) => (current === section ? undefined : current)), 1400);
    showNotification({ color: 'red', message });
  }

  /**
   * Generates the referral PDF (unless an already generated document is passed) and attaches it to the Task.
   * Failures are reported to Sentry and returned so the success state can offer a retry.
   */
  async function generateAndAttachPdf(
    order: MedicationRequest,
    task: Task,
    signer: Practitioner,
    signedTime: string,
    existingDocument?: DocumentReference
  ): Promise<PdfOutcome> {
    let document = existingDocument;
    if (!document) {
      try {
        document = await createReferralDocument(medplum, patient, signer, order, signedTime);
      } catch (error) {
        Sentry.captureException(error);
        showNotification({ color: 'yellow', message: pdfFailureMessage('generate', error) });
        return { failure: 'generate', task };
      }
    }
    try {
      const updatedTask = await medplum.updateResource(withTenantScope(attachReferralDocumentToTask(task, document)));
      return { document, task: updatedTask };
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'yellow', message: pdfFailureMessage('attach', error) });
      return { failure: 'attach', document, task };
    }
  }

  async function retryPdf(): Promise<void> {
    if (!createdOrder || !pdf || !currentPractitioner || !signedAt) {
      return;
    }
    setRetryingPdf(true);
    try {
      const outcome = await generateAndAttachPdf(createdOrder, pdf.task, currentPractitioner, signedAt, pdf.document);
      setPdf(outcome);
      if (!outcome.failure) {
        showNotification({ color: 'green', message: 'Referral PDF generated and attached to the task.' });
      }
    } finally {
      setRetryingPdf(false);
    }
  }

  async function createOrder(): Promise<void> {
    if (!referralType) {
      fail('type', 'Select a referral type before creating the referral.');
      return;
    }
    if (!reason.trim()) {
      fail('reason', 'Enter a reason for the referral.');
      return;
    }
    if (!signedAt) {
      fail('signature', 'Provider signature required');
      return;
    }
    if (!currentPractitioner) {
      fail('signature', 'Signing provider profile is unavailable.');
      return;
    }
    const parsedOrderDate = parseDateOnly(orderDate);
    if (!parsedOrderDate || Number.isNaN(parsedOrderDate.getTime())) {
      fail('date', 'Enter the date of order before creating the referral.');
      return;
    }
    if (!recipient?.reference) {
      fail('task', 'Select an individual task recipient.');
      return;
    }

    setSubmitting(true);
    try {
      // The order, its signature Provenance, and its follow-up Task are written in one transaction so an order can
      // never exist without its signature or its Task.
      const orderFullUrl = `urn:uuid:${crypto.randomUUID()}`;
      parsedOrderDate.setHours(12, 0, 0, 0);
      const orderPayload = withTenantScope<MedicationRequest>({
        resourceType: 'MedicationRequest',
        status: 'active',
        intent: 'order',
        priority,
        subject: createReference(patient),
        encounter: encounter ? createReference(encounter) : undefined,
        requester: createReference(currentPractitioner),
        authoredOn: parsedOrderDate.toISOString(),
        medicationCodeableConcept: {
          coding: [{ system: REFERRAL_SYSTEM, code: referralType, display: referralType }],
          text: `${referralType} · Referral`,
        },
        reasonCode: selectedDiagnosis ? [reasonCodeFor(selectedDiagnosis)] : undefined,
        note: [
          {
            text: [
              `Reason: ${reason.trim()}`,
              referralTarget.trim() ? `Referral target: ${referralTarget.trim()}` : '',
              // "Special instructions" is what the referral PDF prints under CLINICAL NOTES.
              clinicalNotes.trim() ? `Special instructions: ${clinicalNotes.trim()}` : '',
              taskNotes.trim() ? `Task notes: ${taskNotes.trim()}` : '',
            ]
              .filter(Boolean)
              .join('\n'),
          },
        ],
      });
      const signaturePayload = withTenantScope(
        buildOrderSignatureProvenance(orderFullUrl, createReference(currentPractitioner), signedAt)
      );
      const taskPayload = withTenantScope<Task>({
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        priority,
        authoredOn: new Date().toISOString(),
        requester: createReference(currentPractitioner),
        owner: recipient,
        for: createReference(patient),
        encounter: encounter ? createReference(encounter) : undefined,
        focus: { reference: orderFullUrl },
        code: {
          coding: [{ system: REFERRAL_TASK_SYSTEM, code: 'referral-follow-up' }],
          text: referralTaskTitle(referralType),
        },
        description: taskNotes.trim() || reason.trim(),
      });
      const response = await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          { fullUrl: orderFullUrl, resource: orderPayload, request: { method: 'POST', url: 'MedicationRequest' } },
          { resource: signaturePayload, request: { method: 'POST', url: 'Provenance' } },
          { resource: taskPayload, request: { method: 'POST', url: 'Task' } },
        ],
      });
      const order = response.entry?.find((entry: BundleEntry) => entry.resource?.resourceType === 'MedicationRequest')
        ?.resource as MedicationRequest | undefined;
      const createdTask = response.entry?.find((entry: BundleEntry) => entry.resource?.resourceType === 'Task')
        ?.resource as Task | undefined;
      if (!order?.id || !createdTask?.id) {
        throw new Error('Referral was not created: the order or its task is missing from the server response.');
      }

      // The order and task are committed; a PDF failure only leaves the document missing and can be retried.
      const outcome = await generateAndAttachPdf(order, createdTask, currentPractitioner, signedAt);
      setPdf(outcome);
      setCreatedOrder(order);
      onCreated?.(order);
      if (!outcome.failure) {
        showNotification({ color: 'green', message: 'Referral created and task assigned.' });
      }
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: normalizeErrorString(error) });
    } finally {
      setSubmitting(false);
    }
  }

  const isInvalid = (...sections: ReferralSection[]): boolean =>
    invalidSection !== undefined && sections.includes(invalidSection);

  let title: string;
  let subLine: JSX.Element;
  let body: JSX.Element;
  let footer: JSX.Element;

  if (createdOrder) {
    const pdfFailure = pdf?.failure;
    const summary: [string, string][] = [
      ['Referral type', referralType ?? '—'],
      ['Refer to', referralTarget.trim() || 'Task recipient to select'],
      ['Diagnosis', selectedDiagnosis?.display ?? 'None'],
      ['Priority', PRIORITIES.find((entry) => entry.value === priority)?.label ?? priority],
      ['Reason', reason.trim()],
      ['Assigned to', recipient?.display ?? '—'],
      ['Date of order', formatDisplayDate(orderDate) ?? orderDate],
      ['Signed', [signerName, formatDisplayDate(signedAt)].filter(Boolean).join(' · ')],
    ];
    title = 'Referral order placed';
    subLine = (
      <>
        {patientName(patient)}
        {mrn ? ` · ${mrn}` : ''}
      </>
    );
    body = (
      <div className={classes.done}>
        <div className={classes.doneIcon}>
          <IconCheck stroke={2.4} aria-hidden />
        </div>
        <p className={classes.doneText}>
          The referral has been signed and filed to the patient chart. A{' '}
          <b>{referralTaskTitle(referralType ?? 'referral')}</b> task is now assigned to{' '}
          {recipient?.display ?? 'the selected provider'}.
        </p>
        {pdfFailure && (
          <Alert
            className={classes.pdfAlert}
            color="yellow"
            title={pdfFailure === 'attach' ? 'Referral PDF not attached' : 'Referral PDF not generated'}
          >
            <Stack gap="xs" align="flex-start">
              <Text size="sm">
                {pdfFailure === 'attach'
                  ? 'The referral order and task were created and the referral PDF was generated, but it could not be attached to the task.'
                  : 'The referral order and task were created, but the referral PDF could not be generated.'}{' '}
                Retry below, or contact support if the problem persists.
              </Text>
              <Button
                size="xs"
                variant="light"
                color="yellow"
                loading={retryingPdf}
                onClick={() => retryPdf().catch(() => undefined)}
              >
                Retry PDF
              </Button>
            </Stack>
          </Alert>
        )}
        <div className={classes.summary} data-testid="referral-summary">
          {summary.map(([label, value]) => (
            <div className={classes.summaryRow} key={label}>
              <span className={classes.summaryLabel}>{label}</span>
              <span className={classes.summaryValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
    footer = (
      <>
        <div className={classes.note} />
        <div className={classes.buttons}>
          <Button
            variant="default"
            className={`${classes.button} ${classes.buttonSecondary}`}
            leftSection={<IconDownload size={16} aria-hidden />}
            onClick={() => navigate(`/DmeOrder/${patient.id}?prescriptionId=${createdOrder.id}&type=referral`)}
          >
            View referral form
          </Button>
          <Button className={classes.button} onClick={onClose}>
            Done
          </Button>
        </div>
      </>
    );
  } else {
    const npi = practitionerNpi(currentPractitioner);
    const providerPhone = phoneOf(currentPractitioner);
    let diagnosisPlaceholder = 'Select an active condition';
    if (encounter) {
      diagnosisPlaceholder = 'Select from encounter or active conditions';
    } else if (diagnoses.length === 0) {
      diagnosisPlaceholder = 'No active conditions on file';
    }
    title = 'Order Referral';
    subLine = encounter ? (
      <>
        From <b>{encounterTitle(encounter)}</b> · {patientName(patient)}
        {formatDisplayDate(encounter.period?.start) ? ` · ${formatDisplayDate(encounter.period?.start)}` : ''}
      </>
    ) : (
      <>
        Patient chart · <b>{patientName(patient)}</b>
        {mrn ? ` · ${mrn}` : ''}
      </>
    );
    body = (
      <>
        <section className={classes.card} aria-labelledby="referral-patient-heading">
          <div className={classes.cardHeader}>
            <h3 className={classes.cardTitle} id="referral-patient-heading">
              Patient Information
            </h3>
            <span className={classes.autofill}>
              <IconBolt aria-hidden /> From chart
            </span>
          </div>
          <div className={classes.grid}>
            <TextInput
              label="Patient Name"
              withAsterisk
              readOnly
              value={patientName(patient)}
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
            <TextInput
              label="Date of Birth"
              withAsterisk
              readOnly
              value={patient.birthDate ?? ''}
              placeholder="Not on file"
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
            <TextInput
              label="Insurance"
              readOnly
              value={insurance ?? ''}
              placeholder="Not on file"
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
            <TextInput
              label="Phone"
              readOnly
              value={phoneOf(patient) ?? ''}
              placeholder="Not on file"
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
          </div>
        </section>

        <section
          className={classes.card}
          aria-labelledby="referral-details-heading"
          data-testid="referral-details-section"
          data-invalid={isInvalid('type', 'reason') || undefined}
        >
          <div className={classes.cardHeader}>
            <h3 className={classes.cardTitle} id="referral-details-heading">
              Referral Details
            </h3>
          </div>
          <div className={classes.grid}>
            <Select
              label="Referral Type"
              placeholder="Select a referral type"
              required
              data={REFERRAL_TYPES}
              value={referralType}
              onChange={setReferralType}
              error={isInvalid('type')}
              classNames={fieldClassNames({ invalid: isInvalid('type') })}
            />
            <TextInput
              label="Refer To"
              placeholder="Facility or provider name"
              description="Leave blank for the task recipient to choose an in-network provider."
              inputWrapperOrder={FIELD_ORDER}
              value={referralTarget}
              onChange={(event) => setReferralTarget(event.currentTarget.value)}
              classNames={fieldClassNames()}
            />
            <Select
              label="Diagnosis"
              description={
                encounter
                  ? "Optional. From this encounter's diagnoses and the patient's existing conditions."
                  : "Optional. From this patient's active conditions."
              }
              inputWrapperOrder={FIELD_ORDER}
              placeholder={diagnosisPlaceholder}
              searchable
              clearable
              data={diagnosisOptions}
              value={diagnosis}
              onChange={setDiagnosis}
              classNames={fieldClassNames()}
            />
            <TextInput
              label="Reason"
              placeholder="e.g. HSAT non-diagnostic"
              required
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              error={isInvalid('reason')}
              classNames={fieldClassNames({ invalid: isInvalid('reason') })}
            />
          </div>
          <div className={classes.subLabel} id="referral-priority-label">
            Priority
            <span className={classes.required} aria-hidden>
              *
            </span>
          </div>
          <div className={classes.choiceRow} role="radiogroup" aria-labelledby="referral-priority-label">
            {PRIORITIES.map((entry) => (
              <label
                key={entry.value}
                className={`${classes.choice} ${priority === entry.value ? classes.choiceSelected : ''}`}
              >
                <input
                  className={classes.choiceInput}
                  type="radio"
                  name="referral-priority"
                  value={entry.value}
                  checked={priority === entry.value}
                  onChange={() => setPriority(entry.value)}
                />
                <span className={classes.ring} aria-hidden />
                <span>
                  {entry.label} <span className={classes.choiceSub}>{entry.sub}</span>
                </span>
              </label>
            ))}
          </div>
          <div className={`${classes.grid} ${classes.gridSpaced}`}>
            <Textarea
              label="Clinical Notes"
              placeholder="Findings and context the receiving clinic needs. Appears on the referral form."
              minRows={3}
              value={clinicalNotes}
              onChange={(event) => setClinicalNotes(event.currentTarget.value)}
              classNames={fieldClassNames({ span2: true, textarea: true })}
            />
          </div>
        </section>

        <section
          className={classes.card}
          aria-labelledby="referral-task-heading"
          data-testid="referral-task-section"
          data-invalid={isInvalid('task') || undefined}
        >
          <div className={classes.cardHeader}>
            <h3 className={classes.cardTitle} id="referral-task-heading">
              Task Notes
            </h3>
            <span className={classes.autofill}>Internal</span>
          </div>
          <div className={classes.grid}>
            <ProviderSelect
              label="Task Recipient"
              placeholder="Select an individual provider"
              description="An individual provider owns the referral task."
              inputWrapperOrder={FIELD_ORDER}
              required
              allowDeselect={false}
              value={recipient}
              onChange={setRecipient}
              // `undefined` (not false) keeps ProviderSelect's own "Could not load providers" error visible.
              error={isInvalid('task') || undefined}
              classNames={fieldClassNames({ invalid: isInvalid('task') })}
            />
          </div>
          <div className={`${classes.grid} ${classes.gridSpaced}`}>
            <Textarea
              label="Task Notes"
              placeholder="Coordination notes for whoever works this task."
              description="Posts to the referral task. Never printed on the referral form."
              inputWrapperOrder={FIELD_ORDER}
              minRows={3}
              value={taskNotes}
              onChange={(event) => setTaskNotes(event.currentTarget.value)}
              classNames={fieldClassNames({ span2: true, textarea: true })}
            />
          </div>
        </section>

        <section
          className={classes.card}
          aria-labelledby="referral-from-heading"
          data-testid="referral-signature-section"
          data-invalid={isInvalid('signature', 'date') || undefined}
        >
          <div className={classes.cardHeader}>
            <h3 className={classes.cardTitle} id="referral-from-heading">
              Referral From
            </h3>
          </div>
          <div className={classes.grid}>
            <TextInput
              label="Referring Provider"
              withAsterisk
              readOnly
              value={signerName}
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
            {npi && (
              <TextInput
                label="NPI"
                readOnly
                value={npi}
                classNames={fieldClassNames({ variants: ['readonly', 'mono'] })}
              />
            )}
            <TextInput
              label="Practice"
              readOnly
              value={REFERRING_PRACTICE}
              classNames={fieldClassNames({ variants: ['readonly'] })}
            />
            {providerPhone && (
              <TextInput
                label="Phone"
                readOnly
                value={providerPhone}
                classNames={fieldClassNames({ variants: ['readonly'] })}
              />
            )}
          </div>
          <div className={classes.sigRow}>
            <div className={classes.field}>
              <span className={classes.label} id="referral-signature-label">
                Signature
                <span className={classes.required} aria-hidden>
                  *
                </span>
              </span>
              <button
                type="button"
                className={[
                  classes.sigPad,
                  signedAt ? classes.sigPadSigned : undefined,
                  isInvalid('signature') ? classes.invalid : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={Boolean(signedAt)}
                aria-describedby="referral-signature-label"
                aria-label={signedAt ? `Signed by ${signerName}. Click to remove signature` : undefined}
                data-invalid={isInvalid('signature') || undefined}
                data-testid="referral-signature-pad"
                onClick={() => setSignedAt((current) => (current ? undefined : new Date().toISOString()))}
              >
                {signedAt ? (
                  <span className={classes.sigName}>{signerName}</span>
                ) : (
                  <span className={classes.sigPlaceholder}>Click to sign electronically</span>
                )}
              </button>
            </div>
            <TextInput
              label="Date of Order"
              type="date"
              required
              value={orderDate}
              onChange={(event) => setOrderDate(event.currentTarget.value)}
              error={isInvalid('date')}
              classNames={fieldClassNames({ invalid: isInvalid('date') })}
            />
          </div>
        </section>
      </>
    );
    footer = (
      <>
        <div className={classes.note}>
          <IconLock aria-hidden /> Filed to the patient chart on save.
        </div>
        <div className={classes.buttons}>
          <Button variant="default" className={`${classes.button} ${classes.buttonSecondary}`} onClick={onClose}>
            Cancel
          </Button>
          <Button className={classes.button} loading={submitting} onClick={() => createOrder().catch(() => undefined)}>
            Create Referral
          </Button>
        </div>
      </>
    );
  }

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size={940}
      yOffset={44}
      radius={14}
      classNames={{
        content: classes.panel,
        header: classes.head,
        title: classes.title,
        close: classes.close,
        body: classes.body,
      }}
    >
      <Modal.Overlay backgroundOpacity={0.5} color="#100a0e" />
      <Modal.Content>
        <Modal.Header>
          <div className={classes.headMain}>
            <Modal.Title>{title}</Modal.Title>
            <div className={classes.sub}>{subLine}</div>
          </div>
          <Modal.CloseButton aria-label="Close" />
        </Modal.Header>
        <Modal.Body>{body}</Modal.Body>
        <div className={classes.foot}>{footer}</div>
      </Modal.Content>
    </Modal.Root>
  );
}
